import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Badge, Panel } from "@/components/Bits";
import { fmtCompact, fmtDateTime, fmtMoney, STATUS_META, FINAL_STATUS_META } from "@/lib/format";
import { SYSTEM_META, SERVICE_META } from "@/lib/system";
import { ArrowLeft, Send, MessageCircle, AlertCircle, Copy, Clock, ExternalLink, History, Lock, Inbox, Reply, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["NEW", "IN_PROGRESS", "ESCALATED_TO_BANK", "AWAITING_FINAL_STATUS", "RESOLVED", "CLOSED"];
const FINALS = ["", "SUCCESS", "FAILED", "NO_FUNDS", "NOT_FOUND"];

function SlaWidget({ ticket }) {
  const { sla } = ticket;
  const level = sla.bank_level !== "idle" ? sla.bank_level : sla.response_level;
  const seconds = sla.time_since_escalated_sec ?? sla.time_to_first_response_sec ?? 0;
  const label = sla.time_since_escalated_sec != null ? "Since Escalated to Bank" : (sla.first_response_received ? "First Response OK" : "Time to First Response");
  const cls = { ok: "text-emerald-400 border-emerald-500/30", warning: "text-amber-400 border-amber-500/30", breach: "text-rose-400 border-rose-500/30 pulse-dot", idle: "text-slate-400 border-line" }[level];

  return (
    <div className={`border ${cls.split(" ")[1]} ${cls.split(" ")[2] || ""} rounded-sm p-4 bg-surface`} data-testid="sla-widget">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">SLA Timer</span>
        <Clock className={`w-3.5 h-3.5 ${cls.split(" ")[0]}`} />
      </div>
      <div className={`text-3xl font-bold font-mono tabular-nums ${cls.split(" ")[0]}`}>{fmtCompact(seconds)}</div>
      <div className="text-[11px] text-slate-400 mt-1">{label}</div>
      {ticket.final_status_at && (
        <div className="mt-3 pt-3 border-t border-line text-[10px] text-slate-400">
          Final status received: <span className="text-white font-mono">{fmtDateTime(ticket.final_status_at)}</span>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-xs border-b border-line/30 last:border-0">
    <span className="text-slate-500 uppercase tracking-wider text-[10px]">{label}</span>
    <span className={`text-right text-white ${mono ? "font-mono" : ""}`}>{value || <span className="text-slate-600">—</span>}</span>
  </div>
);

export default function TicketDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canEdit = (user?.permissions || []).includes("tickets.edit");
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [tgChats, setTgChats] = useState([]);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(true);
  const [sendToTelegram, setSendToTelegram] = useState(true);
  const [tpLang, setTpLang] = useState("ID");
  const [tick, setTick] = useState(0);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    const [r1, r2, r3, r4, r5, r6] = await Promise.all([
      api.get(`/tickets/${id}`),
      api.get(`/tickets/${id}/comments`),
      api.get(`/tickets/${id}/events`),
      api.get(`/users`),
      api.get(`/templates`),
      api.get(`/telegram/chats`),
    ]);
    setT(r1.data); setComments(r2.data); setEvents(r3.data); setUsers(r4.data); setTemplates(r5.data); setTgChats(r6.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Live tick every second to update SLA timer
  useEffect(() => {
    const i = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(i);
  }, []);

  // Refresh every 15s
  useEffect(() => {
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, [load]);

  const liveTicket = useMemo(() => {
    if (!t) return null;
    const now = Date.now();
    const created = t.created_at ? new Date(t.created_at).getTime() : null;
    const escalated = t.escalated_at ? new Date(t.escalated_at).getTime() : null;
    const final = t.final_status_at ? new Date(t.final_status_at).getTime() : null;
    const firstResp = t.first_response_at ? new Date(t.first_response_at).getTime() : null;
    const sla = { ...t.sla };
    if (escalated) {
      const end = final || now;
      sla.time_since_escalated_sec = Math.floor((end - escalated) / 1000);
    }
    if (!firstResp && created) {
      sla.time_to_first_response_sec = Math.floor((now - created) / 1000);
    }
    return { ...t, sla };
  }, [t, tick]);

  const langTemplates = useMemo(() => templates.filter((x) => x.language === tpLang), [templates, tpLang]);

  if (!t) return <div className="p-8 text-slate-400">Loading...</div>;

  const insertTemplate = (tpl) => {
    const body = tpl.body
      .replaceAll("{ORDER}", t.merchant_order_number || t.platform_order_number || t.order_number || "—")
      .replaceAll("{MERCHANT_ORDER}", t.merchant_order_number || "—")
      .replaceAll("{PLATFORM_ORDER}", t.platform_order_number || "—")
      .replaceAll("{RRN}", t.rrn_number || "—")
      .replaceAll("{VA}", t.va_number || "—");
    setComment(body);
    setInternal(false);
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    const body = { body: comment, internal };
    if (t.telegram_chat_id && !internal && sendToTelegram) body.send_to_telegram = true;
    await api.post(`/tickets/${id}/comments`, body);
    setComment("");
    load();
  };

  const changeStatus = async (status) => {
    await api.post(`/tickets/${id}/status`, { status });
    load();
  };

  const setFinal = async (final_status) => {
    await api.post(`/tickets/${id}/status`, { status: t.status, final_status });
    load();
  };

  const assign = async (uid) => {
    await api.patch(`/tickets/${id}`, { assigned_to: uid || null });
    load();
  };

  const changeSystem = async (sys) => {
    if (!sys || sys === t.system) return;
    await api.patch(`/tickets/${id}`, { system: sys });
    load();
  };

  const attachTelegramChat = async (mappingId) => {
    const chat = tgChats.find((c) => c.id === mappingId);
    await api.patch(`/tickets/${id}`, {
      telegram_chat_id: chat ? chat.chat_id : "",
      telegram_chat_title: chat ? (chat.chat_title || chat.merchant_name) : "",
      telegram_thread_id: chat?.thread_id || null,
      telegram_thread_name: chat?.thread_name || "",
    });
    load();
  };

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="ticket-detail-page">
      <div className="flex items-center justify-between">
        <button data-testid="back-btn" onClick={() => nav("/tickets")} className="text-xs text-slate-400 hover:text-cyan flex items-center gap-1.5">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Tickets
        </button>
        <div className="text-[10px] text-slate-500 font-mono">{t.ticket_number}</div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Badge color={SYSTEM_META[t.system]?.color || "muted"}>{SYSTEM_META[t.system]?.label || "Unknown System"}</Badge>
            <Badge color={SERVICE_META[t.service_type]?.color || "muted"}>{SERVICE_META[t.service_type]?.label || "—"}</Badge>
            {t.merchant_group && <Badge color="muted">{t.merchant_group}</Badge>}
            {t.telegram_chat_id && (
              <Badge color="info">
                TG · {t.telegram_chat_title || t.telegram_chat_id}
                {t.telegram_thread_id ? <> · 🧵 {t.telegram_thread_name || `Topic ${t.telegram_thread_id}`}</> : null}
              </Badge>
            )}
            <Badge color={STATUS_META[t.status]?.color}>{STATUS_META[t.status]?.label}</Badge>
            {t.final_status && <Badge color={FINAL_STATUS_META[t.final_status]?.color}>{FINAL_STATUS_META[t.final_status]?.label}</Badge>}
            <Badge color={t.transaction_type === "QRIS" ? "info" : "awaiting"}>{t.transaction_type}</Badge>
            <Badge color="muted">{t.customer_type}</Badge>
          </div>
          <h1 className="text-xl font-bold tracking-tight">{t.title || `Ticket ${t.ticket_number}`}</h1>
          {t.description && <p className="text-sm text-slate-400 mt-1">{t.description}</p>}
        </div>
        <div className="w-[260px] shrink-0"><SlaWidget ticket={liveTicket} /></div>
      </div>

      {/* Quick actions */}
      <div className="bg-surface border border-line rounded-sm p-3 flex flex-wrap items-center gap-2" data-testid="quick-actions">
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mr-1">Status:</span>
        {STATUSES.map((s) => (
          <button
            key={s}
            data-testid={`set-status-${s}`}
            onClick={() => changeStatus(s)}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-wider rounded-sm border transition-colors ${
              t.status === s ? "bg-cyan/15 border-cyan/40 text-cyan" : "border-line text-slate-400 hover:bg-surface2"
            }`}
          >
            {STATUS_META[s].label}
          </button>
        ))}
        <span className="text-slate-700 mx-1">·</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Final:</span>
        <select
          data-testid="final-status-select"
          value={t.final_status || ""}
          onChange={(e) => setFinal(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-1 text-xs focus:border-cyan focus:outline-none"
        >
          {FINALS.map((f) => <option key={f} value={f}>{FINAL_STATUS_META[f].label}</option>)}
        </select>
        <span className="text-slate-700 mx-1">·</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Assign:</span>
        <select
          data-testid="assign-select"
          value={t.assigned_to || ""}
          onChange={(e) => assign(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-1 text-xs focus:border-cyan focus:outline-none"
        >
          <option value="">Unassigned</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <span className="text-slate-700 mx-1">·</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">System:</span>
        <select
          data-testid="system-select-detail"
          value={t.system || "TARAPAY"}
          onChange={(e) => changeSystem(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-1 text-xs focus:border-cyan focus:outline-none"
        >
          <option value="TARAPAY">Tarapay</option>
          <option value="SATURNUS">Saturnuspay</option>
        </select>
        <span className="text-slate-700 mx-1">·</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">TG Chat / Topic:</span>
        <select
          data-testid="tg-chat-attach"
          value={(() => {
            const m = tgChats.find((c) => c.chat_id === t.telegram_chat_id && (c.thread_id || null) === (t.telegram_thread_id || null));
            return m ? m.id : "";
          })()}
          onChange={(e) => attachTelegramChat(e.target.value)}
          className="bg-bg border border-line rounded-sm px-2 py-1 text-xs focus:border-cyan focus:outline-none max-w-[260px]"
        >
          <option value="">— Not linked —</option>
          {tgChats.map((c) => (
            <option key={c.id} value={c.id}>
              {c.chat_title || c.merchant_name}
              {c.thread_id ? ` · 🧵 ${c.thread_name || `Topic ${c.thread_id}`}` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Transaction info + Comments */}
        <div className="lg:col-span-2 space-y-4">
          <Panel title="Transaction Details" testid="panel-tx" action={
            canEdit ? (
              <button
                data-testid="edit-ticket-btn"
                onClick={() => setEditOpen(true)}
                className="text-xs text-cyan hover:text-cyan-dark inline-flex items-center gap-1 px-2 py-1 border border-line rounded-sm hover:bg-surface2"
                title="Koreksi salah input (RRN, nominal, dll)"
              >
                <Pencil className="w-3 h-3" /> Edit Fields
              </button>
            ) : null
          }>
            <div className="grid grid-cols-2 gap-x-6">
              <Row label="Merchant Order #" value={t.merchant_order_number || t.order_number} mono />
              <Row label="Platform Order #" value={t.platform_order_number} mono />
              <Row label="RRN Number" value={t.rrn_number} mono />
              <Row label="VA Number" value={t.va_number} mono />
              <Row label="NMID" value={t.nmid} mono />
              <Row label="Merchant" value={t.merchant_name} />
              <Row label="Payment Channel" value={t.payment_channel} />
              <Row label="Amount" value={fmtMoney(t.amount)} mono />
              <Row label="Transaction Date" value={t.transaction_date} mono />
              <Row label="Customer" value={t.customer_name} />
              <Row label="Contact" value={t.customer_contact} />
              <Row label="Source" value={t.source} />
              <Row label="Language" value={t.language} />
            </div>
            {t.proof_url && (
              <a data-testid="proof-link" href={t.proof_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-cyan hover:underline">
                <ExternalLink className="w-3 h-3" /> View transaction proof
              </a>
            )}
          </Panel>

          <Panel title={`Conversation (${comments.length})`} testid="panel-comments">
            <div className="space-y-2 max-h-[28rem] overflow-y-auto pr-1" data-testid="comments-list">
              {comments.length === 0 && <div className="text-xs text-slate-500 py-4 text-center">Belum ada komunikasi.</div>}
              {comments.map((c) => {
                const dir = c.direction || (c.internal ? "INTERNAL" : ((c.by === null && (c.by_name || "").startsWith("Telegram")) ? "INBOUND" : "OUTBOUND"));
                if (dir === "INTERNAL") {
                  return (
                    <div key={c.id} data-testid={`comment-${c.id}`} data-direction="internal" className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-3">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Lock className="w-3 h-3 text-amber-400" />
                          <span className="text-[11px] font-semibold text-amber-200">{c.by_name}</span>
                          <Badge color="warning" className="!text-[9px] !px-1.5 !py-0">Internal Note</Badge>
                        </div>
                        <span className="text-[10px] text-slate-500 font-mono">{fmtDateTime(c.at)}</span>
                      </div>
                      <pre className="text-xs text-amber-50/90 whitespace-pre-wrap font-sans">{c.body}</pre>
                    </div>
                  );
                }
                if (dir === "INBOUND") {
                  return (
                    <div key={c.id} data-testid={`comment-${c.id}`} data-direction="inbound" className="flex justify-start">
                      <div className="max-w-[85%] border border-emerald-500/30 bg-emerald-500/10 rounded-sm rounded-tl-none p-3">
                        <div className="flex items-center justify-between gap-3 mb-1">
                          <div className="flex items-center gap-2">
                            <Inbox className="w-3 h-3 text-emerald-400" />
                            <span className="text-[11px] font-semibold text-emerald-200">{c.by_name}</span>
                            <Badge color="success" className="!text-[9px] !px-1.5 !py-0">From Customer</Badge>
                          </div>
                          <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">{fmtDateTime(c.at)}</span>
                        </div>
                        <pre className="text-xs text-emerald-50/90 whitespace-pre-wrap font-sans">{c.body}</pre>
                      </div>
                    </div>
                  );
                }
                // OUTBOUND — CS reply to customer
                return (
                  <div key={c.id} data-testid={`comment-${c.id}`} data-direction="outbound" className="flex justify-end">
                    <div className="max-w-[85%] border border-cyan/30 bg-cyan/10 rounded-sm rounded-tr-none p-3">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <div className="flex items-center gap-2">
                          <Reply className="w-3 h-3 text-cyan" />
                          <span className="text-[11px] font-semibold text-cyan">{c.by_name}</span>
                          <Badge color="info" className="!text-[9px] !px-1.5 !py-0">Reply to Customer</Badge>
                          {c.telegram_message_id && <Badge color="muted" className="!text-[9px] !px-1.5 !py-0">via TG</Badge>}
                        </div>
                        <span className="text-[10px] text-slate-500 font-mono whitespace-nowrap">{fmtDateTime(c.at)}</span>
                      </div>
                      <pre className="text-xs text-cyan-50/90 whitespace-pre-wrap font-sans">{c.body}</pre>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-line space-y-2">
              <div className="flex items-center justify-between text-[10px] uppercase tracking-wider">
                <span className="text-slate-500">Post as:</span>
                <Badge color={internal ? "warning" : (sendToTelegram && t.telegram_chat_id ? "info" : "muted")} className="!text-[10px]">
                  {internal ? "Internal Note (amber)" : (sendToTelegram && t.telegram_chat_id ? "Reply to Customer · sends via Telegram (cyan)" : "Reply to Customer · no TG send (cyan)")}
                </Badge>
              </div>
              <textarea
                data-testid="comment-textarea"
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={internal ? "Tulis catatan internal (tidak dikirim ke customer)..." : "Tulis balasan ke customer..."}
                className={`w-full bg-bg border rounded-sm px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 ${
                  internal ? "border-amber-500/30 focus:border-amber-500 focus:ring-amber-500/40"
                           : "border-cyan/30 focus:border-cyan focus:ring-cyan/40"
                }`}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      data-testid="comment-internal-toggle"
                      type="checkbox"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                      className="accent-cyan"
                    />
                    Internal note
                  </label>
                  {t.telegram_chat_id && !internal && (
                    <label className="flex items-center gap-2 text-xs cursor-pointer text-cyan" data-testid="tg-reply-toggle-label">
                      <input
                        data-testid="send-to-telegram-toggle"
                        type="checkbox"
                        checked={sendToTelegram}
                        onChange={(e) => setSendToTelegram(e.target.checked)}
                        className="accent-cyan"
                      />
                      Kirim ke Telegram group
                    </label>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="copy-comment-btn"
                    type="button"
                    disabled={!comment}
                    onClick={() => { navigator.clipboard.writeText(comment); }}
                    className="border border-line px-3 py-1.5 text-xs rounded-sm hover:bg-surface2 flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                  <button
                    data-testid="submit-comment-btn"
                    onClick={submitComment}
                    disabled={!comment.trim()}
                    className="bg-cyan text-bg font-semibold px-3 py-1.5 text-xs rounded-sm flex items-center gap-1.5 hover:bg-cyan-dark disabled:opacity-50"
                  >
                    <Send className="w-3 h-3" /> Post
                  </button>
                </div>
              </div>
            </div>
          </Panel>
        </div>

        {/* Right: Templates + Timeline */}
        <div className="space-y-4">
          <Panel title="Response Templates" testid="panel-templates">
            <div className="flex gap-1 mb-3" data-testid="tpl-lang-tabs">
              {["ID", "ZH", "EN"].map((l) => (
                <button
                  key={l}
                  data-testid={`tpl-lang-${l}`}
                  onClick={() => setTpLang(l)}
                  className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider rounded-sm border ${
                    tpLang === l ? "bg-cyan/10 border-cyan/40 text-cyan" : "border-line text-slate-400"
                  }`}
                >
                  {l === "ZH" ? "中文" : l === "ID" ? "Indonesia" : "English"}
                </button>
              ))}
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
              {langTemplates.length === 0 && <div className="text-xs text-slate-500 text-center py-3">No templates.</div>}
              {langTemplates.map((tpl) => (
                <button
                  key={tpl.id}
                  data-testid={`tpl-${tpl.code}-${tpl.language}`}
                  onClick={() => insertTemplate(tpl)}
                  className="w-full text-left border border-line hover:border-cyan/40 hover:bg-surface2 rounded-sm p-2 transition-colors"
                >
                  <div className="text-[11px] font-semibold text-cyan">{tpl.label}</div>
                  <div className="text-[10px] text-slate-500 font-mono mt-0.5">{tpl.code}</div>
                </button>
              ))}
            </div>
          </Panel>

          <Panel title="Timeline" testid="panel-timeline">
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1" data-testid="events-list">
              {events.map((e) => (
                <div key={e.id} className="flex gap-2 text-xs">
                  <History className="w-3 h-3 text-slate-500 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200">
                      <span className="text-cyan">{e.by_name}</span> · {e.type === "created" ? "created the ticket" : e.type === "status_change" ? `changed status to ${e.data?.status}` : `updated ${e.data?.fields?.join(", ")}`}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{fmtDateTime(e.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>

      {editOpen && (
        <EditTicketModal
          ticket={t}
          onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); await load(); toast.success("Ticket diupdate"); }}
        />
      )}
    </div>
  );
}


function EditTicketModal({ ticket, onClose, onSaved }) {
  const EDITABLE_FIELDS = [
    { key: "merchant_order_number", label: "Merchant Order Number", placeholder: "MO-...", mono: true },
    { key: "platform_order_number", label: "Platform Order Number", placeholder: "PAYIN/PAYOUT/WD...", mono: true },
    { key: "rrn_number", label: "RRN Number", placeholder: "010000HF...", mono: true },
    { key: "va_number", label: "VA Number", placeholder: "8801234567890", mono: true },
    { key: "nmid", label: "NMID", placeholder: "ID...", mono: true },
    { key: "merchant_name", label: "Merchant Name" },
    { key: "payment_channel", label: "Payment Channel", placeholder: "BCA / Mandiri / QRIS / etc" },
    { key: "amount", label: "Amount (Rp)", type: "number", mono: true },
    { key: "transaction_date", label: "Transaction Date (YYYY-MM-DD HH:MM)", mono: true, placeholder: "2026-02-24 14:30" },
    { key: "customer_name", label: "Customer Name" },
    { key: "customer_contact", label: "Customer Contact" },
    { key: "language", label: "Language", placeholder: "ID / EN / ZH" },
    { key: "proof_url", label: "Proof URL" },
  ];

  const initial = {};
  for (const f of EDITABLE_FIELDS) initial[f.key] = ticket[f.key] ?? "";
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      // Only send fields that actually changed (or were cleared from non-empty)
      const payload = {};
      for (const f of EDITABLE_FIELDS) {
        const before = ticket[f.key] ?? "";
        const after = form[f.key];
        if (String(before) === String(after)) continue;
        // numeric coercion
        if (f.type === "number") {
          payload[f.key] = after === "" ? null : Number(after);
        } else {
          payload[f.key] = after === "" ? null : after;
        }
      }
      if (Object.keys(payload).length === 0) {
        setErr("Tidak ada perubahan untuk disimpan."); setBusy(false); return;
      }
      await api.patch(`/tickets/${ticket.id}`, payload);
      onSaved();
    } catch (e2) {
      setErr(formatErr(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-surface border border-line rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto p-5 space-y-3"
        data-testid="edit-ticket-modal"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cyan">Edit Ticket Fields · {ticket.ticket_number}</div>
            <h2 className="text-lg font-bold flex items-center gap-2 mt-0.5">
              <Pencil className="w-4 h-4 text-cyan" /> Koreksi Salah Input
            </h2>
            <p className="text-[11px] text-slate-400 mt-0.5">Hanya field di bawah yang bisa di-edit. Perubahan tercatat di Timeline.</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {EDITABLE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="block text-[10px] uppercase tracking-wider text-slate-400 mb-1">{f.label}</label>
              <input
                data-testid={`edit-${f.key}`}
                type={f.type === "number" ? "number" : "text"}
                value={form[f.key] ?? ""}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                placeholder={f.placeholder || ""}
                className={`w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40 ${f.mono ? "font-mono" : ""}`}
              />
            </div>
          ))}
        </div>

        {err && (
          <div className="px-3 py-2 border border-rose-500/30 bg-rose-500/10 text-rose-400 text-xs rounded-sm" data-testid="edit-ticket-error">
            {err}
          </div>
        )}

        <div className="flex gap-2 pt-2 border-t border-line/40">
          <button type="button" onClick={onClose} className="flex-1 bg-bg border border-line py-2 rounded-sm text-sm hover:bg-surface2">
            Batal
          </button>
          <button
            type="submit"
            disabled={busy}
            data-testid="edit-ticket-save"
            className="flex-1 bg-cyan hover:bg-cyan-dark text-bg font-semibold py-2 rounded-sm text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </form>
    </div>
  );
}
